import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Inbox, Package, CheckCircle2, Clock } from "lucide-react";
import api from "@/lib/api";
import { formatPrice } from "@/lib/format";

const INQ_LABEL = {
  noua: { label: "Nouă", cls: "bg-[#D4AF37]/15 text-[#B5952F]" },
  contactata: { label: "Contactată", cls: "bg-blue-50 text-blue-700" },
  finalizata: { label: "Finalizată", cls: "bg-green-50 text-green-700" },
  respinsa: { label: "Respinsă", cls: "bg-red-50 text-red-600" },
};

const STAT_LABEL = {
  nou: "Disponibilă",
  rezervata: "Rezervată",
  vanduta: "Vândută",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-gray-100 animate-pulse rounded-2xl h-28" />
        ))}
      </div>
    );
  }

  const cards = [
    { icon: Car, label: "Total mașini", value: stats.total_vehicles, tone: "black" },
    { icon: Package, label: "Disponibile", value: stats.available, tone: "gold" },
    { icon: Clock, label: "Rezervate", value: stats.reserved, tone: "gray" },
    { icon: CheckCircle2, label: "Vândute", value: stats.sold, tone: "dark" },
    { icon: Inbox, label: "Solicitări noi", value: stats.new_inquiries, tone: "gold" },
    { icon: Inbox, label: "Total solicitări", value: stats.total_inquiries, tone: "black" },
  ];

  return (
    <div>
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Overview</div>
        <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#0A0A0A]">Bine ai revenit</h1>
        <p className="mt-1 text-sm text-gray-500">Situația curentă a parcului auto AutoRIO.</p>
      </div>

      <div className="mt-6 sm:mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
            <div
              className={`h-9 w-9 sm:h-10 sm:w-10 rounded-xl flex items-center justify-center mb-3 sm:mb-4 ${
                c.tone === "gold"
                  ? "bg-[#D4AF37] text-white"
                  : c.tone === "gray"
                    ? "bg-gray-100 text-gray-600"
                    : c.tone === "dark"
                      ? "bg-[#0A0A0A] text-[#D4AF37]"
                      : "bg-gray-900 text-white"
              }`}
            >
              <c.icon className="h-4 w-4" />
            </div>
            <div className="text-xl sm:text-2xl font-extrabold text-[#0A0A0A]">{c.value}</div>
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.14em] text-gray-400 font-bold mt-1 leading-tight">
              {c.label}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 sm:mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-2xl border border-gray-100">
          <div className="p-4 sm:p-5 border-b border-gray-100 flex items-center justify-between">
            <div className="text-base font-bold text-[#0A0A0A]">Ultimele solicitări</div>
            <Link to="/admin/solicitari" className="text-xs font-semibold text-[#B5952F]">
              Vezi toate
            </Link>
          </div>
          <ul className="divide-y divide-gray-100">
            {stats.latest_inquiries.length === 0 && (
              <li className="p-6 text-sm text-gray-400 text-center">Nicio solicitare încă.</li>
            )}
            {stats.latest_inquiries.map((i) => (
              <li key={i.id} className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#0A0A0A] truncate">{i.name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {i.phone}
                    {i.vehicle_label ? ` · ${i.vehicle_label}` : ""}
                  </div>
                </div>
                <span
                  className={`text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded-full shrink-0 ${INQ_LABEL[i.status]?.cls || ""}`}
                >
                  {INQ_LABEL[i.status]?.label || i.status}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100">
          <div className="p-4 sm:p-5 border-b border-gray-100 flex items-center justify-between">
            <div className="text-base font-bold text-[#0A0A0A]">Ultimele mașini</div>
            <Link to="/admin/masini" className="text-xs font-semibold text-[#B5952F]">
              Vezi toate
            </Link>
          </div>
          <ul className="divide-y divide-gray-100">
            {stats.latest_vehicles.length === 0 && (
              <li className="p-6 text-sm text-gray-400 text-center">Nicio mașină încă.</li>
            )}
            {stats.latest_vehicles.map((v) => (
              <li key={v.id} className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#0A0A0A] truncate">
                    {v.brand} {v.model}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {v.year} · {formatPrice(v.price)} · {STAT_LABEL[v.status]}
                  </div>
                </div>
                <Link
                  to={`/admin/masini/${v.id}/editeaza`}
                  className="text-xs font-semibold text-[#B5952F] shrink-0"
                >
                  Editează
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
